import React, { useState, useEffect } from 'react';
import config from 'config.json';
import useFetch from 'hooks/useFetch';
import MpPartialForm from 'components/Mercadopago/MpPartialForm';
import { useNavigate } from 'react-router-dom';

interface PreferenceData {
    preference_code: string;
}

const PartialPayment: React.FC = () => {
    const [errorMessage, setErrorMessage] = useState('');
    const [showPaymentForm, setShowPaymentForm] = useState(false);
    const [paymentAmount, setPaymentAmount] = useState('');

    const navigate = useNavigate();

    const id = new URLSearchParams(window.location.search).get('reservationId');
    sessionStorage.setItem('booking_id', id || '');
    const completePaymentTotal = parseFloat(sessionStorage.getItem('complete_payment_total') || '0');

    const [partialPaymentRequest, setPartialPaymentRequest] = useState({
        id_reserva: '',
        monto: '',
    });

    const { data: preferenceData, error: preferenceError, fetchData: fetchPreferenceData } = useFetch<PreferenceData>(
        'POST',
        `${config.backend_url}/setia/reserva/pago_parcial`,
        partialPaymentRequest,
        {
            'Content-Type': 'application/json',
            'SetiaAuthorization': `${sessionStorage.getItem('user_token')}`
        }
    );

    useEffect(() => {
        if (preferenceData) {
            sessionStorage.setItem('complete_payment_id', preferenceData.preference_code);
            sessionStorage.setItem('complete_payment_amount', paymentAmount);
            setShowPaymentForm(true);
        }
    }, [preferenceData, paymentAmount]);

    useEffect(() => {
        if (id) {
            setPartialPaymentRequest((prev) => ({ ...prev, id_reserva: id }));
        }
    }, [id]);

    useEffect(() => {
        if (partialPaymentRequest.monto) {
            fetchPreferenceData();
        }
    }, [partialPaymentRequest]);

    const handleGeneratePayment = () => {
        const amount = parseFloat(paymentAmount);
        if (isNaN(amount) || amount <= 0 || amount > completePaymentTotal) {
            setErrorMessage('El monto ingresado no es válido o supera el monto pendiente.');
            return;
        }
        setErrorMessage('');

        // Actualiza el estado con el monto antes de realizar la llamada a la API
        setPartialPaymentRequest((prev) => ({
            ...prev,
            monto: paymentAmount
        }));
    };

    const handleBack = () => {
        navigate(`/reservation-account-status?reservationId=${id}`);
    };

    const handleRedirectToMerecadoPago = () => {
        sessionStorage.setItem('id_reserva_mp', id || '');
        const id_preferencia = sessionStorage.getItem("complete_payment_id");
        if (!id_preferencia) {
            alert("No se pudo obtener el ID de pago. Por favor, inténtalo de nuevo más tarde. ");
        }
        const url = `${config.mp_form}${id_preferencia}`;
        window.location.href = url;
    }

    return (
        <div className="flex justify-center items-center min-h-screen bg-gray-100 p-4 w-full">
            <div className="container mx-auto my-4 bg-white p-6 rounded-md shadow-md max-w-md w-full">
                <h1 className="text-3xl font-semibold text-neutral-800 dark:text-neutral-200 text-center mb-4 w-full">
                    Completa tu pago
                </h1>
                <div className="w-full">
                    {showPaymentForm ? (
                        <>
                            <p className="text-blue-500 mt-2">Serás redirigido a Mercado Pago para realizar el pago</p>
                            <button className="border mt-4 w-full px-4 py-1.5 pb-3 bg-blue-600 hover:bg-blue-800 text-white text-md sm:px-6 sm:py-2.5 rounded-full focus:outline-none selected" onClick={handleRedirectToMerecadoPago} >
                                Ir al formulario de pago
                            </button>
                        </>
                    ) : (
                        <div className="flex flex-col items-center w-full">
                            <input
                                type="number"
                                value={paymentAmount}
                                onChange={(e) => setPaymentAmount(e.target.value)}
                                placeholder="Monto a pagar"
                                className="input border p-2 rounded-md w-full max-w-xs"
                            />
                            <button
                                onClick={handleGeneratePayment}
                                className="button bg-blue-500 text-white p-2 rounded-md mt-2 w-full"
                                disabled={!paymentAmount} // Deshabilita el botón si no hay monto
                            >
                                Generar Pago Parcial
                            </button>
                            {errorMessage && <div className="text-red-500 mt-2 w-full">{errorMessage}</div>}
                        </div>
                    )}
                </div>
                {preferenceError && <div className="text-red-500 mt-2 w-full">{preferenceError.message || 'Error al generar la preferencia de pago.'}</div>}
                <button onClick={handleBack} className="button bg-gray-500 text-white p-2 rounded-md mt-4 w-full">
                    Volver
                </button>
            </div>
        </div>
    );
};

export default PartialPayment;